import React, { useState, useEffect } from "react";
import DashboardHeader from "../../components/DashboardHeader";
import Sidebar from "../../components/Sidebar";
import "./AccountantEntries.css";
import {
  createNewInvoiceApi,
  getSalesListApi,
  getExpenseListApi,
  saveExpenseApi,
  getMileageListApi,
  recordMileageApi,
  getBankStatementsListApi,
  createBankStatementApi,
  takeImageUploadSalesApi,
  takeImageUploadExpenseApi,
} from "../../services/apiCall";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { usePagination } from "../../hooks/usePagination";
import Pagination from "../../components/Pagination";
import CommonLoader from "../../components/CommonLoader";

const AccountantEntries = () => {
  const [activeTab, setActiveTab] = useState("sales");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [isExtracting, setIsExtracting] = useState(false);

  // Reset extracted data when modal opens/closes
  useEffect(() => {
    if (!isAddModalOpen) setExtractedData(null);
  }, [isAddModalOpen]);

  const userId = useSelector((state) => state?.user?.userId);
  const businessId = useSelector((state) => state?.user?.businessId);

  // Define fetch functions for each tab
  const fetchSalesFn = React.useCallback(async () => {
    if (!businessId) return [];
    const response = await getSalesListApi({ businessId });
    return response?.data?.sales || [];
  }, [businessId]);

  const fetchExpensesFn = React.useCallback(async () => {
    if (!businessId) return [];
    const res = await getExpenseListApi({ businessId });
    return res?.data?.expenses || [];
  }, [businessId]);

  const fetchMileageFn = React.useCallback(async () => {
    if (!businessId) return [];
    const res = await getMileageListApi({ businessId });
    return res?.data?.mileage || [];
  }, [businessId]);

  const fetchBankFn = React.useCallback(async () => {
    if (!businessId) return [];
    const res = await getBankStatementsListApi({ businessId });
    return res?.data?.statements || [];
  }, [businessId]);

  // Initialize pagination hooks for each tab
  const salesPagination = usePagination(fetchSalesFn, {
    dependencies: [businessId],
  });
  const expensesPagination = usePagination(fetchExpensesFn, {
    dependencies: [businessId],
  });
  const mileagePagination = usePagination(fetchMileageFn, {
    dependencies: [businessId],
  });
  const bankPagination = usePagination(fetchBankFn, {
    dependencies: [businessId],
  });

  // Get current pagination object based on active tab
  const getCurrentPagination = () => {
    switch (activeTab) {
      case "sales":
        return salesPagination;
      case "expenses":
        return expensesPagination;
      case "mileage":
        return mileagePagination;
      case "bank":
        return bankPagination;
      default:
        return salesPagination;
    }
  };

  const {
    displayedData: currentData,
    data: fullData,
    loading: isPaginationLoading,
    page,
    totalPages,
    goToPage,
    refresh,
    updateLocalData,
    itemsPerPage,
    changeItemsPerPage,
    totalItems,
  } = getCurrentPagination();

  const isLoading = isPaginationLoading || isActionLoading;

  const getAddButtonText = () =>
    ({
      sales: "Add Sales",
      expenses: "Add Expense",
      mileage: "Add Mileage",
      bank: "Add Bank Transaction",
    })[activeTab];

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsExtracting(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("userId", userId);
    formData.append("businessId", businessId);

    try {
      let response;
      if (activeTab === "sales") {
        formData.append("type", "sale");
        response = await takeImageUploadSalesApi(formData);
      } else {
        const typeMap = {
          expenses: "expense",
          mileage: "mileage",
          bank: "bank",
        };
        formData.append("type", typeMap[activeTab]);
        response = await takeImageUploadExpenseApi(formData);
      }

      if (response?.data?.data) {
        setExtractedData(response.data.data);
        toast.success("Document analyzed successfully!");
      } else {
        toast.info("Could not extract data automatically. Please fill manually.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to parse document.");
    } finally {
      setIsExtracting(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const values = Object.fromEntries(formData.entries());

    if (activeTab === "sales") {
      const gross = parseFloat(values.gross) || 0;
      const vat = gross * 0.2;
      const net = gross - vat;

      const payload = {
        businessId,
        createdBy: userId,
        invoiceNumber:
          values.invoiceNo || `INV-${Date.now().toString().slice(-6)}`,
        gross,
        vat,
        net,
      };
      console.log("payload", payload);

      try {
        setIsActionLoading(true);
        const response = await createNewInvoiceApi(payload);

        if (response?.data) {
          toast.success("Sale entry created successfully!");
          refresh(); // Refresh list
        }
      } catch (err) {
        toast.error("Failed to create sale.");
      } finally {
        setIsAddModalOpen(false);
        setIsActionLoading(false);
      }

      return;
    }

    if (activeTab === "expenses") {
      const gross = parseFloat(values.gross) || 0;
      const vat = gross * 0.2;
      const net = gross - vat;

      const payload = {
        businessId,
        createdBy: userId,
        category: values.category,
        description: values.narrative,
        gross,
        vat,
        net,
        transactionDate: values.date,
        employeeName: values.employee,
      };

      try {
        setIsActionLoading(true);
        const res = await saveExpenseApi(payload);
        console.log("res::::::", res);
        toast.success("Expense added!");
        refresh();
        setIsAddModalOpen(false);
      } catch (err) {
        toast.error("Failed to save expense");
      } finally {
        setIsActionLoading(false);
      }
    }

    if (activeTab === "mileage") {
      const miles = parseFloat(values.miles) || 0;
      if (miles <= 0) {
        toast.error("Miles must be greater than 0");
        return;
      }

      const amount = (miles * 0.45).toFixed(2);

      const payload = {
        businessId,
        createdBy: userId,
        reason: values.reason,
        description: values.description,
        from: values.from,
        to: values.to,
        miles: miles,
        amount: parseFloat(amount),
      };
      console.log("paylaod", payload);
      try {
        setIsActionLoading(true);
        const res = await recordMileageApi(payload);
        console.log("res::::::", res);
        if (res?.data?.mileage) {
          toast.success("Mileage recorded!");
          refresh();
          setIsAddModalOpen(false);
        }
      } catch (err) {
        toast.error("Failed to save mileage");
      } finally {
        setIsActionLoading(false);
      }
      return;
    }

    if (activeTab === "bank") {
      const amount = parseFloat(values.amount) || 0;
      if (amount === 0) {
        toast.error("Amount cannot be zero");
        return;
      }

      const payload = {
        businessId,
        createdBy: userId,
        transactionDate: values.date,
        referenceNumber: values.referenceNumber
          ? values.referenceNumber
          : "123456",
        transactionType: values.type === "credit" ? "CREDIT" : "DEBIT",
        amount: Math.abs(amount),
        balance: parseFloat(values.balance) || 0,
        category: values.category || "Uncategorized",
        description: values.description || "",
      };
      console.log("payload", payload);
      try {
        setIsActionLoading(true);
        const res = await createBankStatementApi(payload);
        console.log("res::::::", res);
        if (res?.data?.statement) {
          toast.success("Bank transaction added!");
          refresh();
          setIsAddModalOpen(false);
        }
      } catch (err) {
        toast.error("Failed to save bank transaction");
      } finally {
        setIsActionLoading(false);
      }
      return;
    }
    setIsAddModalOpen(false);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const values = Object.fromEntries(formData);
    const updatedId = editItem.id;

    let updatedItem = {};

    if (activeTab === "sales") {
      // Sale update logic if needed
    }

    if (activeTab === "expenses") {
      const gross = parseFloat(values.gross) || 0;
      const vat = gross * 0.2;
      const net = gross - vat;
      updatedItem = {
        id: updatedId,
        date: values.date,
        category: values.category,
        narrative: values.narrative,
        gross: gross.toFixed(2),
        vat: vat.toFixed(2),
        net: net.toFixed(2),
        employee: values.employee,
      };
      updateLocalData(
        fullData.map((i) => (i.id === updatedId ? updatedItem : i)),
      );
    }

    if (activeTab === "mileage") {
      const miles = parseFloat(values.miles) || 0;
      const amount = (miles * 0.45).toFixed(2);
      updatedItem = {
        id: updatedId,
        date: values.date,
        client: values.client,
        reason: values.reason,
        from: values.from,
        to: values.to,
        miles,
        amount,
        employee: values.employee,
      };
      updateLocalData(
        fullData.map((i) => (i.id === updatedId ? updatedItem : i)),
      );
    }

    if (activeTab === "bank") {
      updatedItem = {
        id: updatedId,
        date: values.date,
        category: values.category,
        narrative: values.narrative,
        amount: parseFloat(values.amount).toFixed(2),
      };
      updateLocalData(
        fullData.map((i) => (i.id === updatedId ? updatedItem : i)),
      );
    }

    setIsEditModalOpen(false);
    setEditItem(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this entry?")) return;

    updateLocalData(fullData.filter((i) => i.id !== id));
  };

  const handleView = (item) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setIsEditModalOpen(true);
  };

  const renderFormFields = (isEdit = false) => {
    const item = isEdit ? editItem : (extractedData || {});

    const commonFields = (
      <>
        {!isEdit && (
          <div className="form-group" style={{ marginBottom: ".5rem" }}>
            <label style={{ fontSize: "14px", fontWeight: "600", color: "#4b5068", display: "block", marginBottom: "8px" }}>
              Auto-fill from Document (Optional)
            </label>
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileUpload}
              className="form-input"
              style={{ padding: "8px", background: "#f8fafc" }}
            />
            {isExtracting && <span style={{ color: "#3b82f6", fontSize: "13px", marginTop: "4px", display: "inline-block" }}>Extracting data...</span>}
          </div>
        )}
        <div className="form-group">
          <input
            type="date"
            name="date"
            required
            defaultValue={isEdit ? item.date : (item.createdAt ? new Date(item.createdAt).toISOString().split("T")[0] : "")}
            className="form-input"
          />
        </div>
      </>
    );

    if (activeTab === "sales") {
      return (
        <>
          {commonFields}
          <div className="form-group">
            <input
              type="text"
              name="client"
              placeholder="Client Name"
              required
              defaultValue={isEdit ? item.client : ""}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <input
              type="text"
              name="invoiceNo"
              placeholder="Invoice Number (optional)"
              defaultValue={isEdit ? item.invoiceNumber : (item.invoiceNumber || "")}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <input
              type="number"
              name="gross"
              step="0.01"
              placeholder="Gross Amount (£)"
              required
              defaultValue={isEdit ? item.gross : (item.gross || "")}
              className="form-input"
            />
            {/* <small>VAT (20%) and Net will be calculated automatically</small> */}
          </div>
        </>
      );
    }

    if (activeTab === "expenses") {
      return (
        <>
          {commonFields}
          <div className="form-group">
            <input
              type="text"
              name="category"
              placeholder="Category"
              required
              defaultValue={isEdit ? item.category : (item.category || "")}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <input
              type="text"
              name="narrative"
              placeholder="Description/Narrative"
              required
              defaultValue={isEdit ? item.narrative : (item.description || "")}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <input
              type="number"
              name="gross"
              step="0.01"
              placeholder="Gross Amount (£)"
              required
              defaultValue={isEdit ? item.gross : (item.gross || "")}
              className="form-input"
            />
            {/* <small>VAT & Net auto-calculated</small> */}
          </div>
          <div className="form-group">
            <input
              type="text"
              name="employee"
              placeholder="Employee Name"
              required
              defaultValue={isEdit ? item.employee : ""}
              className="form-input"
            />
          </div>
        </>
      );
    }

    if (activeTab === "mileage") {
      return (
        <>
          {commonFields}
          <div className="form-group">
            <input
              type="text"
              name="client"
              placeholder="Client"
              defaultValue={isEdit ? item.client : ""}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <input
              type="text"
              name="reason"
              placeholder="Reason (e.g. Meeting)"
              required
              defaultValue={isEdit ? item.reason : (item.reason || "")}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <input
              type="text"
              name="from"
              placeholder="From Location"
              required
              defaultValue={isEdit ? item.from : (item.from || "")}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <input
              type="text"
              name="to"
              placeholder="To Location"
              required
              defaultValue={isEdit ? item.to : (item.to || "")}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <input
              type="number"
              name="miles"
              placeholder="Miles Driven"
              required
              defaultValue={isEdit ? item.miles : (item.miles || "")}
              className="form-input"
            />
            {/* <small>Rate: £0.45/mile → Amount auto-calculated</small> */}
          </div>
          <div className="form-group">
            <input
              type="text"
              name="employee"
              placeholder="Employee"
              required
              defaultValue={isEdit ? item.employee : ""}
              className="form-input"
            />
          </div>
        </>
      );
    }

    if (activeTab === "bank") {
      return (
        <>
          {commonFields}
          <div className="form-group">
            <select
              name="category"
              required
              defaultValue={isEdit ? item.category : ""}
              className="form-input"
            >
              <option value="">Select Category</option>
              <option>Client Payment</option>
              <option>Supplier Payment</option>
              <option>Bank Fee</option>
              <option>Transfer</option>
              <option>Other Income</option>
            </select>
          </div>
          <div className="form-group">
            <input
              type="text"
              name="narrative"
              placeholder="Description"
              required
              defaultValue={isEdit ? item.narrative : (item.description || "")}
              className="form-input"
            />
          </div>
          <div className="form-group">
            <input
              type="number"
              name="amount"
              step="0.01"
              placeholder="Amount (£) – use negative for outgoing"
              required
              defaultValue={isEdit ? item.amount : (item.amount || "")}
              className="form-input"
            />
          </div>
        </>
      );
    }

    return null;
  };

  const renderTable = () => {
    if (!Array.isArray(currentData)) return null;

    if (activeTab === "sales") {
      return (
        <table className="entries-table">
          <thead>
            <tr>
              <th>DOCUMENT</th>
              <th>DATE</th>
              <th>CLIENT</th>
              <th>INVOICE NUMBER</th>
              <th>GROSS</th>
              <th>VAT</th>
              <th>NET</th>
              <th className="actions-header">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {currentData.map((item) => (
              <tr key={item.id}>
                <td>
                  <button className="view-btn" onClick={() => handleView(item)}>
                    View
                  </button>
                </td>
                <td>{item.createdAt}</td>
                <td>{item.invoiceNumber}</td>
                <td>{item.invoiceNumber}</td>
                <td>£{item.gross}</td>
                <td>£{item.vat}</td>
                <td>£{item.net}</td>
                <td className="actions">
                  <button className="edit-btn" onClick={() => handleEdit(item)}>
                    Edit
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => handleDelete(item.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (activeTab === "expenses") {
      return (
        <table className="entries-table">
          <thead>
            <tr>
              <th>DOCUMENT</th>
              <th>DATE</th>
              <th>CATEGORY</th>
              <th>NARRATIVE</th>
              <th>GROSS</th>
              <th>VAT</th>
              <th>NET</th>
              <th>EMPLOYEE</th>
              <th className="actions-header">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {currentData.map((item) => (
              <tr key={item.id}>
                <td>
                  <button className="view-btn" onClick={() => handleView(item)}>
                    View
                  </button>
                </td>
                <td>{item.createdAt}</td>
                <td>{item["expenseCategory.name"]}</td>
                <td>{item.description}</td>
                <td>£{item.gross}</td>
                <td>£{item.vat}</td>
                <td>£{item.net}</td>
                <td>{item.description}</td>
                <td className="actions">
                  <button className="edit-btn" onClick={() => handleEdit(item)}>
                    Edit
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => handleDelete(item.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (activeTab === "mileage") {
      return (
        <table className="entries-table">
          <thead>
            <tr>
              <th>DOCUMENT</th>
              <th>DATE</th>
              <th>CLIENT</th>
              <th>REASON</th>
              <th>FROM</th>
              <th>TO</th>
              <th>MILES</th>
              <th>AMOUNT</th>
              <th>EMPLOYEE</th>
              <th className="actions-header">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {currentData.map((item) => (
              <tr key={item.id}>
                <td>
                  <button className="view-btn" onClick={() => handleView(item)}>
                    View
                  </button>
                </td>
                <td>{item.createdAt}</td>
                <td>{item.client}</td>
                <td>{item.reason}</td>
                <td>{item.from}</td>
                <td>{item.to}</td>
                <td>{item.miles}</td>
                <td>£{item.amount}</td>
                <td>{item.employee}</td>
                <td className="actions">
                  <button className="edit-btn" onClick={() => handleEdit(item)}>
                    Edit
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => handleDelete(item.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (activeTab === "bank") {
      return (
        <table className="entries-table">
          <thead>
            <tr>
              <th>DOCUMENT</th>
              <th>DATE</th>
              <th>TRANSACTION TYPE</th>
              <th>NARRATIVE</th>
              <th>AMOUNT</th>
              <th className="actions-header">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {currentData.map((item) => (
              <tr key={item.id}>
                <td>
                  <button className="view-btn" onClick={() => handleView(item)}>
                    View
                  </button>
                </td>
                <td>{item.createdAt}</td>
                <td>{item.transactionType}</td>
                <td>{item.description}</td>
                <td
                  style={{
                    color: item.amount.startsWith("-") ? "#dc2626" : "#16a34a",
                    fontWeight: 600,
                  }}
                >
                  £{Math.abs(item.amount).toFixed(2)}
                </td>
                <td className="actions">
                  <button className="edit-btn" onClick={() => handleEdit(item)}>
                    Edit
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => handleDelete(item.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    return null;
  };

  return (
    <div className="app">
      <Sidebar />
      <CommonLoader visible={isLoading} />
      <main className="main">
        <DashboardHeader
          title="Entries"
          subtitle="Uploaded Entries by the company"
        />

        <div className="entries-container">
          <div className="entries-header">
            <h2>Entries</h2>
            <button
              className="add-sales-btn"
              onClick={() => setIsAddModalOpen(true)}
            >
              + {getAddButtonText()}
            </button>
          </div>

          <div className="tabs">
            <button
              className={`tab ${activeTab === "sales" ? "active" : ""}`}
              onClick={() => setActiveTab("sales")}
            >
              Sales
            </button>
            <button
              className={`tab ${activeTab === "expenses" ? "active" : ""}`}
              onClick={() => setActiveTab("expenses")}
            >
              Expenses
            </button>
            <button
              className={`tab ${activeTab === "mileage" ? "active" : ""}`}
              onClick={() => setActiveTab("mileage")}
            >
              Mileage
            </button>
            <button
              className={`tab ${activeTab === "bank" ? "active" : ""}`}
              onClick={() => setActiveTab("bank")}
            >
              Bank
            </button>
          </div>

          <div className="table-container">
            {renderTable()}
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={goToPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageSizeChange={changeItemsPerPage}
            />
          </div>
        </div>
      </main>

      {/* ADD MODAL */}
      {isAddModalOpen && (
        <div className="modal-overlays" onClick={() => setIsAddModalOpen(false)}>
          <div
            className="modal-contents"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: "90vh", overflowY: "auto" }}
          >
            <h3 className="modal-title">{getAddButtonText()}</h3>
            <form className="modal-form" onSubmit={handleSave} key={extractedData ? "filled" : "empty"}>
              <div className="file-upload-section">
                <label className="file-upload-label">
                  Upload Document for Auto-fill:
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    accept="image/*,.pdf"
                  />
                </label>
              </div>
              {renderFormFields(false)}
              <div className="modal-footer">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="save-button">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {isEditModalOpen && editItem && (
        <div
          className="modal-overlays"
          onClick={() => setIsEditModalOpen(false)}
        >
          <div className="modal-contents" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">
              Edit {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
            </h2>
            <form onSubmit={handleUpdate} className="modal-form">
              {renderFormFields(true)}
              <div className="modal-footer">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="save-button">
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* BEAUTIFUL PDF-STYLE VIEW MODAL */}
      {isViewModalOpen && selectedItem && (
        <div
          className="modal-overlays"
          onClick={() => setIsViewModalOpen(false)}
        >
          <div
            className="preview-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="preview-header">
              <h2>
                {activeTab === "sales" && "Sales Invoice"}
                {activeTab === "expenses" && "Expense Receipt"}
                {activeTab === "mileage" && "Mileage Claim"}
                {activeTab === "bank" && "Bank Transaction"}
              </h2>
              <div className="preview-actions">
                <button onClick={() => window.print()} className="download-btn">
                  Download PDF
                </button>
                <button
                  className="close-btn"
                  onClick={() => setIsViewModalOpen(false)}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Invoice Paper Style */}
            <div className="invoice-paper">
              <div className="company-header">
                <h1>ABC Tech Ltd.</h1>
                <p>
                  123 Business Street
                  <br />
                  London, EC1A 1BB
                  <br />
                  hello@abctech.co.uk
                </p>
              </div>

              <div className="invoice-details-grid">
                {activeTab === "sales" && (
                  <>
                    <div>
                      <strong>Invoice To:</strong>
                      <p>{selectedItem.client}</p>
                    </div>
                    <div>
                      <strong>Invoice No:</strong>
                      <p>{selectedItem.invoiceNo}</p>
                      <strong>Date:</strong>
                      <p>{selectedItem.date}</p>
                    </div>
                  </>
                )}

                {activeTab === "expenses" && (
                  <>
                    <div>
                      <strong>Employee:</strong>
                      <p>{selectedItem.employee}</p>
                      <strong>Category:</strong>
                      <p>{selectedItem.category}</p>
                    </div>
                    <div>
                      <strong>Date:</strong>
                      <p>{selectedItem.date}</p>
                      <strong>Description:</strong>
                      <p>{selectedItem.narrative}</p>
                    </div>
                  </>
                )}

                {activeTab === "mileage" && (
                  <>
                    <div>
                      <strong>Employee:</strong>
                      <p>{selectedItem.employee}</p>
                      <strong>Client:</strong>
                      <p>{selectedItem.client}</p>
                    </div>
                    <div>
                      <strong>Date:</strong>
                      <p>{selectedItem.date}</p>
                      <strong>Route:</strong>
                      <p>
                        {selectedItem.from} → {selectedItem.to}
                      </p>
                    </div>
                  </>
                )}

                {activeTab === "bank" && (
                  <>
                    <div>
                      <strong>Category:</strong>
                      <p>{selectedItem.category}</p>
                    </div>
                    <div>
                      <strong>Date:</strong>
                      <p>{selectedItem.date}</p>
                      <strong>Description:</strong>
                      <p>{selectedItem.narrative}</p>
                    </div>
                  </>
                )}
              </div>

              {/* Amounts Table */}
              <table className="amounts-table">
                <tbody>
                  {(activeTab === "sales" || activeTab === "expenses") && (
                    <>
                      <tr>
                        <td>Net Amount</td>
                        <td className="amount">
                          {selectedItem.net || "£0.00"}
                        </td>
                      </tr>
                      <tr>
                        <td>VAT (20%)</td>
                        <td className="amount">
                          {selectedItem.vat || "£0.00"}
                        </td>
                      </tr>
                      <tr className="total-row">
                        <td>
                          <strong>Total (Gross)</strong>
                        </td>
                        <td className="amount total">
                          <strong>
                            {selectedItem.gross || selectedItem.amount}
                          </strong>
                        </td>
                      </tr>
                    </>
                  )}

                  {activeTab === "mileage" && (
                    <tr className="total-row">
                      <td>
                        <strong>Mileage Allowance</strong>
                      </td>
                      <td className="amount total">
                        <strong>{selectedItem.miles}</strong>
                      </td>
                    </tr>
                  )}

                  {activeTab === "bank" && (
                    <tr
                      className={
                        selectedItem.amount.startsWith("-")
                          ? "negative"
                          : "positive"
                      }
                    >
                      <td>
                        <strong>Amount</strong>
                      </td>
                      <td className="amount total">
                        <strong>{selectedItem.amount}</strong>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="thank-you-note">
                <p>Thank you for your business!</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountantEntries;
